/**
 * SFrame100 – Settlement Entry Point / Role Selection
 * Figma Frame 0 (node 1018:14784)
 *
 * "Welcome. How can we best support you today?"
 *  - "I need help right now. Someone has recently passed." -> SFrame101
 *  - "I'm planning ahead for myself." -> Frame134 (existing estate planning)
 *
 * This is a public page (no auth required).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import radioChecked from '../../assets/images/radio-checked.svg';
import radioUnchecked from '../../assets/images/radio-unchecked.svg';

type PathChoice = 'settlement' | 'planning' | null;

export default function SFrame100() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<PathChoice>(null);

  const handleNext = () => {
    if (selected === 'settlement') {
      navigate('/sframe-101');
    } else if (selected === 'planning') {
      navigate('/frame-134');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Brand logo */}
      <div className="flex justify-center pt-6">
        <BrandSymbol size="lg" showText />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col items-center px-6 pt-12 pb-32">
        <h1 className="font-display text-2xl sm:text-3xl text-blue-900 text-center mb-16">
          Welcome. How can we best support you today?
        </h1>

        {/* Radio options */}
        <div className="max-w-lg w-full space-y-4">
          <label
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => setSelected('settlement')}
          >
            <img
              src={selected === 'settlement' ? radioChecked : radioUnchecked}
              alt=""
              className="w-[18px] h-[18px] shrink-0"
            />
            <span className="text-base text-black font-normal">
              I need help right now. Someone has recently passed.
            </span>
          </label>

          <label
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => setSelected('planning')}
          >
            <img
              src={selected === 'planning' ? radioChecked : radioUnchecked}
              alt=""
              className="w-[18px] h-[18px] shrink-0"
            />
            <span className="text-base text-black font-normal">
              I'm planning ahead for myself.
            </span>
          </label>
        </div>
      </div>

      {/* Bottom progress bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-blue-900 text-white-alt px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <p className="font-semibold text-base">Welcome</p>
            <p className="text-sm text-blue-100 opacity-80">Step 0/3</p>
          </div>
          <button
            onClick={handleNext}
            disabled={!selected}
            className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium transition-all ${
              selected
                ? 'bg-white-alt/10 hover:bg-white-alt/20 text-white-alt cursor-pointer'
                : 'opacity-40 cursor-not-allowed text-white-alt/60'
            }`}
          >
            Next
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        {/* Progress track */}
        <div className="max-w-3xl mx-auto mt-2">
          <div className="w-full bg-white-alt/20 rounded-full h-1">
            <div className="bg-green-600 h-1 rounded-full" style={{ width: '2%' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
